'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Divider } from '@/app/components/divider';
import { Container } from '@/app/components/layouts/container';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { CompareHeaderCtaList } from '@/app/lcv/components/sections/compare/list/header-cta';
import { CompareSummaryList } from '@/app/lcv/components/sections/compare/list/summary';
import { CompareTogglesList } from '@/app/lcv/components/sections/compare/list/toggles';
import { resolveCompareColumnsFromQueryParams } from '@/app/lcv/components/sections/compare/query-resolver';
import { CompareRemark } from '@/app/lcv/components/sections/compare/remark';
import { CompareSelects } from '@/app/lcv/components/sections/compare/selects';
import { trackCompareSelect } from '@/app/lcv/components/sections/compare/tracking';
import type { LcvCompareProps } from '@/app/lcv/components/sections/compare/types';
import { createModelQueryParam, getLcvModelColorId } from '@/app/lcv/components/sections/compare/utils';
import type { LcvModelColor } from '@/types/tis-cms/types';
const MAX_COLUMN = 3;
export const Compare = ({
  optionGroups,
  defaultModelKeys,
  lineupModelsGroup,
  modelYear,
  modelCategories,
  allModels,
  defaultGradesByCategoryKey,
  defaultModelCategory
}: LcvCompareProps) => {
  const pathname = usePathname();
  const isMobile = useMediaQuery('(max-width: 1023px)');
  const [selectedValues, setSelectedValues] = useState<string[]>(['', '', '']);
  const [selectedColors, setSelectedColors] = useState<(LcvModelColor | undefined)[]>([undefined, undefined, undefined]);
  const listProps = {
    values: selectedValues,
    lineupModelsGroup
  };
  const updateQueryUrl = (values: string[], colors: (LcvModelColor | undefined)[]) => {
    const queryUrl = values.map((val, index) => `model=${createModelQueryParam(val, getLcvModelColorId(colors[index]))}`).join('&');
    window.history.replaceState(null, '', `${pathname}?${queryUrl}`);
  };
  const initFromResolved = (modelKeys: string[], colors: (LcvModelColor | undefined)[]) => {
    setSelectedValues(modelKeys);
    setSelectedColors(colors);
  };
  const initFromDefaultModelKeys = () => {
    const modelKeys = defaultModelKeys.slice(0, MAX_COLUMN);
    const colors = modelKeys.map(key => lineupModelsGroup[key]?.defaultColor ?? undefined);
    while (modelKeys.length < MAX_COLUMN) {
      modelKeys.push('');
      colors.push(undefined);
    }
    initFromResolved(modelKeys, colors);
  };
  const updateModelsAndColors = (values: string[]) => {
    const colorsPayload = values.map((val, index) => {
      const isSelectedValue = selectedValues[index] === val;
      const defaultColor = lineupModelsGroup[val]?.defaultColor ?? undefined;
      return isSelectedValue ? selectedColors[index] : defaultColor;
    });
    setSelectedValues(values);
    setSelectedColors(colorsPayload);
    updateQueryUrl(values, colorsPayload);
    trackCompareSelect({
      modelsGroup: lineupModelsGroup,
      modelKeys: values,
      modelColors: selectedColors
    });
  };
  const onSelectOption = (index: number, value: string) => {
    const values = selectedValues.slice();
    values[index] = value;
    updateModelsAndColors(values);
  };
  const onSelectColor = (index: number, colorItem: LcvModelColor) => {
    const colors = selectedColors.slice();
    colors[index] = colorItem;
    setSelectedColors(colors);
    updateQueryUrl(selectedValues, colors);
  };
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const queryValues = searchParams.getAll('model');
    if (queryValues.length === 0) {
      initFromDefaultModelKeys();
      return;
    }
    const {
      modelKeys,
      colors
    } = resolveCompareColumnsFromQueryParams(queryValues, {
      allModels,
      modelCategories,
      defaultModelCategory,
      modelYear,
      defaultGradesByCategoryKey,
      maxColumns: MAX_COLUMN
    });
    initFromResolved(modelKeys, colors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!isMobile || !selectedValues[2]) return;
    const mobileSelectedValues = selectedValues.slice(0, 2);
    setSelectedValues([...mobileSelectedValues, '']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile]);
  return <Container>
      <CompareSelects values={selectedValues} optionGroups={optionGroups} isMobile={!!isMobile} onSelectOption={onSelectOption} />
      <CompareHeaderCtaList {...listProps} selectedColors={selectedColors} onSelectColor={onSelectColor} />
      <CompareSummaryList {...listProps} />
      <Divider size="none" />
      <CompareTogglesList {...listProps} />
      <CompareRemark />
    </Container>;
};